import { Component, DestroyRef, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { defer, Observable } from 'rxjs';
import { AnalyticsService } from '@app/core/services/analytics/analytics.service';
import { LayoutService } from '@app/storybook/services/layout/layout.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import {
  CreateSmartCartPayload,
  SmartCartListItem,
  SmartCartService,
  UpdateSmartCartPayload,
} from '../smart-cart.service';
import { SmartCartBlock, SmartCartBlockType, SMART_CART_BLOCK_DEFINITIONS } from './smart-cart-block.model';
import {
  SmartCartBlockEditEvent,
  SmartCartFormOrderManagementComponent,
} from './components/smart-cart-form-order-management/smart-cart-form-order-management.component';
import { LoadedWidgetsBySection, SmartCartFormTab, SmartCartFormTabConfig } from './smart-cart-form.model';
import {
  GlobalSettingsFormShape,
  createGlobalSettingsFormGroup,
} from './components/smart-cart-form-global-settings/global-settings.model';
import { SmartCartPreviewConfig } from './preview/widget-previews/widget-preview.types';
import { AUTO_APPLIED_ORDER_DISCOUNT_WITH_DISPLAY_CONDITIONS_ERROR } from './widget-editors/goals-progress-bar/goals-progress-bar-auto-apply-validation';

@Component({
  selector: 'smart-cart-form',
  templateUrl: './smart-cart-form.component.html',
  styleUrls: ['./smart-cart-form.component.scss'],
  standalone: false,
})
export class SmartCartFormComponent implements OnInit, OnDestroy {
  @ViewChild(SmartCartFormOrderManagementComponent) orderManagement?: SmartCartFormOrderManagementComponent;

  private readonly destroyRef = inject(DestroyRef);
  private readonly openedAt = performance.now();
  private lastEditedBlockId = '';

  readonly SmartCartFormTab = SmartCartFormTab;
  readonly SmartCartBlockType = SmartCartBlockType;

  private readonly EDITABLE_BLOCK_TYPES = new Set<SmartCartBlockType>([
    SmartCartBlockType.TOP_BAR,
    SmartCartBlockType.ANNOUNCEMENT_BAR,
    SmartCartBlockType.GOALS_PROGRESS_BAR,
    SmartCartBlockType.CART_SUBTOTAL,
    SmartCartBlockType.CHECKOUT_AREA,
    SmartCartBlockType.DISCOUNT_CODES,
    SmartCartBlockType.CART_ITEMS,
    SmartCartBlockType.DIGITAL_UPSELL,
    SmartCartBlockType.UPSELL_ITEMS,
    SmartCartBlockType.ORDER_PROTECTION,
  ]);

  tabs: SmartCartFormTabConfig[] = [
    { label: 'Order & Management', value: SmartCartFormTab.ORDER_MANAGEMENT },
    { label: 'Global Settings', value: SmartCartFormTab.GLOBAL_SETTINGS },
  ];
  activeTab: SmartCartFormTab = SmartCartFormTab.ORDER_MANAGEMENT;
  formGroup!: FormGroup;
  globalSettingsFormGroup!: FormGroup<GlobalSettingsFormShape>;
  smartCartId: number | null = null;
  editingBlock: SmartCartBlock | null = null;
  editingFormGroup: FormGroup | null = null;
  loadedWidgets: LoadedWidgetsBySection | null = null;
  saving = false;
  isEditingBlockChanged = (): boolean => !!this.orderManagement?.changedByBlockId[this.lastEditedBlockId];

  get previewConfig(): SmartCartPreviewConfig | null {
    if (!this.formGroup || !this.globalSettingsFormGroup || !this.orderManagement) return null;
    return {
      global: this.globalSettingsFormGroup.getRawValue(),
      sections: this.orderManagement.getPreviewWidgetsBySection(),
    };
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private smartCartService: SmartCartService,
    private toastService: ToastService,
    private layoutService: LayoutService,
    private elementRef: ElementRef<HTMLElement>,
    private analytics: AnalyticsService,
  ) {}

  ngOnInit(): void {
    this.globalSettingsFormGroup = createGlobalSettingsFormGroup();
    this.formGroup = new FormGroup({
      internalName: new FormControl<string>('Smart Cart', { nonNullable: true, validators: [Validators.required] }),
      internalDescription: new FormControl<string>('', { nonNullable: true }),
      global: this.globalSettingsFormGroup,
    });

    const idParam = this.route.snapshot.params['id'];
    const id = idParam ? Number(idParam) : NaN;
    if (!isNaN(id)) {
      this.smartCartId = id;
      this.loadSmartCart(id);
    } else {
      // New cart
      this.loadedWidgets = { header: [], body: [], footer: [] };
    }
  }

  ngOnDestroy(): void {
    this.clearBlockEditorHeader();
  }

  onTabChange(tab: SmartCartFormTab): void {
    this.activeTab = tab;
  }

  onEditBlock(event: SmartCartBlockEditEvent): void {
    if (!this.EDITABLE_BLOCK_TYPES.has(event.block.type)) {
      return;
    }
    this.editingBlock = event.block;
    this.lastEditedBlockId = event.block.id;
    this.editingFormGroup = event.formGroup;
    this.layoutService.customHeaderTitle$.set(SMART_CART_BLOCK_DEFINITIONS[event.block.type].label);
    this.layoutService.headerBackAction$.set(() => this.onCloseEditor());
    this.scrollEditorToTop();
  }

  onCloseEditor(): void {
    if (this.editingBlock) this.orderManagement?.refreshChangedState(this.editingBlock.id);
    this.editingBlock = null;
    this.editingFormGroup = null;
    this.clearBlockEditorHeader();
  }

  save(): void {
    if (this.saving) return;
    const save$ = this.analytics.trackSave(
      this.formGroup,
      this.openedAt,
      defer(() => this.runSave()),
    );
    // Widget settings groups are registered under this form (order-management `blockSettings`),
    // so `invalid`/`markAllAsTouched` already cover them.
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      const blockSettingsGroup = this.formGroup.get('blockSettings') as FormGroup | null;
      const hasAutoAppliedOrderDiscountWithDisplayConditions = Object.values(blockSettingsGroup?.controls ?? {}).some(
        (blockSettings) => blockSettings.hasError(AUTO_APPLIED_ORDER_DISCOUNT_WITH_DISPLAY_CONDITIONS_ERROR),
      );
      this.toastService.showToast({
        mode: 'danger',
        message: hasAutoAppliedOrderDiscountWithDisplayConditions
          ? 'A Goals Progress Bar auto-applies an order discount and has display conditions. Remove the conditions or turn off auto-apply.'
          : 'Please fix the highlighted fields before saving',
      });
      return;
    }
    this.saving = true;

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving = false;
        this.toastService.showToast({ mode: 'success', message: 'Smart Cart saved' });
        this.router.navigate(['/engage/smart-cart']);
      },
      error: (err) => {
        this.saving = false;
        console.error('Failed to save smart cart:', err);
        this.toastService.showToast({ mode: 'danger', message: 'Failed to save Smart Cart' });
      },
    });
  }

  private runSave(): Observable<SmartCartListItem> {
    const widgets = this.orderManagement?.getWidgetsBySection() ?? { header: [], body: [], footer: [] };
    const { internalName, internalDescription } = this.formGroup.getRawValue();
    const description: string | null = internalDescription?.trim() ? internalDescription : null;
    const global = this.globalSettingsFormGroup.getRawValue();

    if (this.smartCartId === null) {
      const payload: CreateSmartCartPayload = {
        name: internalName,
        description,
        global,
        ...widgets,
      };
      return this.smartCartService.create(payload);
    }

    const payload: UpdateSmartCartPayload = {
      name: internalName,
      description,
      global,
      ...widgets,
    };
    return this.smartCartService.update(this.smartCartId, payload);
  }

  private clearBlockEditorHeader(): void {
    this.layoutService.customHeaderTitle$.set('');
    this.layoutService.headerBackAction$.set(null);
  }

  private scrollEditorToTop(): void {
    setTimeout(() => {
      const container = this.findScrollableAncestor(this.elementRef.nativeElement);
      if (container) container.scrollTop = 0;
      window.scrollTo({ top: 0 });
    });
  }

  private findScrollableAncestor(element: HTMLElement): HTMLElement | null {
    let node: HTMLElement | null = element.parentElement;
    while (node) {
      const overflowY = getComputedStyle(node).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  private loadSmartCart(id: number): void {
    this.smartCartService
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (cart) => {
          this.formGroup.patchValue({
            internalName: cart.name ?? '',
            internalDescription: cart.description ?? '',
          });
          if (cart.global) {
            this.globalSettingsFormGroup.patchValue(cart.global);
          }
          this.loadedWidgets = {
            header: cart.header ?? [],
            body: cart.body ?? [],
            footer: cart.footer ?? [],
          };
        },
        error: (err) => {
          console.error('Failed to load smart cart:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to load smart cart' });
        },
      });
  }
}
